import CloseSmall from '@crehana/compass.icons.outline/CloseSmall';
import File from '@crehana/compass.icons.outline/File';
import Info from '@crehana/compass.icons.outline/Info';
import Refresh from '@crehana/compass.icons.outline/Refresh';
import Trash2 from '@crehana/compass.icons.outline/Trash2';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crehana/compass.ui/v2';
import { bytesToSize } from '@crehana/utils';

import { useTranslationCreateAndEditEmailTemplates } from '@/shared/components/email-component/hooks/use-translations';

import type { IconProps } from '@crehana/compass.icons.outline/types';
import { ProgressSection } from './progress-section';

// Define component props
export const FileAttached: React.FC<{
  size: number; // File size in bytes
  name: string; // File name
  onClick: () => void; // Function to handle file removal
  progress?: number; // Upload progress percentage (optional)
  isLoading?: boolean; // Indicates if file is being uploaded
  isError?: boolean; // Indicates if an error occurred during upload
  handleRetry: () => void; // Function to retry upload on error
  IconView?: React.FC<React.PropsWithChildren<IconProps>>;
  IconDelete?: React.FC<React.PropsWithChildren<IconProps>>;
  viewSize?: boolean;
}> = ({
  size,
  name,
  onClick,
  progress,
  isLoading,
  isError,
  handleRetry,
  IconView = File,
  IconDelete = Trash2,
  viewSize = true,
}) => {
  const { t } = useTranslationCreateAndEditEmailTemplates(); // Translation hook

  // Function to return content based on the file state
  const returnContent = () => {
    if (isError) {
      return (
        <>
          {/* Error state: displays an error message and a retry button */}
          <div className="tw-flex tw-gap-1.2 tw-items-center">
            {/* Error icon */}
            <span className="tw-w-2.4 tw-h-2.4 tw-text-alert-500">
              <Info size={24} className="tw-text-current" />
            </span>

            {/* Error message and retry button */}
            <section className="tw-flex tw-items-center tw-gap-1.2">
              <p className="tw-font-body5 tw-text-alert-500">
                {t('NOTIFICATION_ERROR_LABEL')}
              </p>
              <button
                className="tw-btn-tertiary-xs tw-flex tw-gap-0.4 tw-text-primary-light-500"
                type="button"
                onClick={handleRetry}
              >
                {/* Retry icon */}
                <span className="tw-w-1.6 tw-h-1.6 ">
                  <Refresh size={16} className="tw-text-current" />
                </span>
                {t('NOTIFICATION_ATTACHMENT_RETRY')}
              </button>
            </section>
          </div>

          {/* Close button to remove the file entry */}
          <button
            type="button"
            className="tw-w-2 tw-h-2 tw-text-neutral-light-700"
            onClick={onClick}
          >
            <CloseSmall size={20} className="tw-text-current" />
          </button>
        </>
      );
    }

    if (isLoading) {
      return (
        /* Loading state: displays a loading icon and progress bar */
        <section className="tw-flex tw-gap-1.2 tw-items-center">
          {/* File icon during loading */}
          <span className="tw-w-2.4 tw-h-2.4 tw-text-neutral-light-600">
            <File size={24} className="tw-text-current" />
          </span>

          {/* Progress bar component */}
          <ProgressSection
            progress={progress || 0}
            label={t('UPLOADING_FILE')}
          />
        </section>
      );
    }

    return (
      <>
        {/* Default state: displays file details and delete button */}
        {/* File icon */}
        <span className="tw-w-2.4 tw-h-2.4 tw-text-primary-light-500">
          <IconView size={24} className="tw-text-current" />
        </span>

        {/* File name and size */}
        <section className="tw-min-w-0 tw-overflow-hidden">
          <p className="tw-text-neutral-light-800 tw-font-link1 tw-font-semibold tw-mb-0.4 tw-nowrap tw-overflow-hidden tw-overflow-ellipsis tw-w-full tw-break-all tw-line-clamp-1">
            {name}
          </p>
          {viewSize && (
            <p className="tw-text-neutral-light-700 tw-font-body5 tw-nowrap tw-overflow-hidden tw-overflow-ellipsis tw-w-full tw-break-all tw-line-clamp-1">
              {bytesToSize(size)}
            </p>
          )}
        </section>

        {/* Delete button */}
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger
              type="button"
              className="tw-w-2 tw-h-2 tw-text-neutral-light-700 tw-flex-shrink-0 tw-min-w-2"
              onClick={onClick}
            >
              <IconDelete
                size={20}
                className="tw-text-current  hover:tw-text-primary-light-500"
              />
            </TooltipTrigger>
            <TooltipContent align="center" side="top">
              <p>{t('DELETE_FILE')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </>
    );
  };

  return (
    <article className="tw-overflow-hidden tw-bg-neutral-light-200 tw-grid tw-grid-cols-[auto_1fr_auto] tw-items-center tw-gap-1.2 tw-px-2 tw-py-1.2 tw-rounded-1 tw-w-full tw-max-w-full">
      {returnContent()}
    </article>
  );
};
